import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { ConfigField } from "config/adminConfig";
import adminConfigService, { UploadConfig } from "services/AdminConfig/adminConfigService";
import HeaderButtons from "components/Button/HeaderButtons";
import { fieldCls } from "components/InlineTable";
// NOTE: shared resolver + toaster, so a save reports its real outcome instead
// of silently replacing the server's reason with a fixed string.
import { getApiErrorMessage } from "util/errorMessage";
import { useToaster } from "contexts/toasterContext";

/**
 * Editor for a module's upload policy — max files + max file size.
 *
 * Unlike the name-list configs (GenericConfigDetail), this is a single object:
 *   GET  api/upload-config/{MODULE}
 *   PUT  api/upload-config/{MODULE}  { maxFiles, maxFileSizeKb }
 *
 * `field.apiEndpoint` already carries the full module path (e.g.
 * "api/upload-config/ASSET"). The allowed file types shown here are read-only —
 * they're managed in the sibling "File Type" tab.
 */
export const UploadConfigDetail: React.FC<{ field: ConfigField; embedded?: boolean }> = ({
  field,
  embedded = false,
}) => {
  const navigate = useNavigate();
  const { addToast } = useToaster();

  const [config, setConfig] = useState<UploadConfig | null>(null);
  const [maxFiles, setMaxFiles] = useState("");
  const [maxFileSizeKb, setMaxFileSizeKb] = useState("");
  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{ maxFiles?: string; maxFileSizeKb?: string }>({});

  useEffect(() => {
    setPageLoading(true);
    setApiError("");
    adminConfigService
      .getUploadConfig(field.apiEndpoint)
      .then((res) => {
        setConfig(res.data);
        setMaxFiles(String(res.data.maxFiles ?? ""));
        setMaxFileSizeKb(String(res.data.maxFileSizeKb ?? ""));
      })
      // A load failure keeps the inline banner: there is no form to toast from
      // and the message must persist while the editor is unusable.
      .catch((err) => setApiError(getApiErrorMessage(err, "Failed to load upload configuration.")))
      .finally(() => setPageLoading(false));
  }, [field.apiEndpoint]);

  const filesNum = Number(maxFiles);
  const sizeNum = Number(maxFileSizeKb);
  // Live MB helper so admins (who think in MB) can sanity-check the KB value.
  const mbPreview = sizeNum > 0 ? (sizeNum / 1024).toFixed(2) : "0";

  const isDirty =
    !!config && (filesNum !== config.maxFiles || sizeNum !== config.maxFileSizeKb);

  const validate = () => {
    const errs: typeof fieldErrors = {};
    if (!Number.isInteger(filesNum) || filesNum < 1) {
      errs.maxFiles = "Enter a whole number of 1 or more.";
    }
    if (!Number.isInteger(sizeNum) || sizeNum < 1) {
      errs.maxFileSizeKb = "Enter a size in KB of 1 or more.";
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setApiError("");
    try {
      const res = await adminConfigService.updateUploadConfig(field.apiEndpoint, {
        maxFiles: filesNum,
        maxFileSizeKb: sizeNum,
      });
      // Prefer the server echo; fall back to the submitted values so the form
      // reflects the save even if the endpoint returns no body.
      const next: UploadConfig =
        res?.data && typeof res.data === "object" && "maxFiles" in res.data
          ? res.data
          : {
              ...(config as UploadConfig),
              maxFiles: filesNum,
              maxFileSizeKb: sizeNum,
              maxFileSizeMb: Number((sizeNum / 1024).toFixed(2)),
            };
      setConfig(next);
      setMaxFiles(String(next.maxFiles));
      setMaxFileSizeKb(String(next.maxFileSizeKb));
      setFieldErrors({});
      addToast("Upload configuration updated successfully", "success");
    } catch (err) {
      addToast(getApiErrorMessage(err, "Failed to save upload configuration."), "error");
    } finally {
      setSaving(false);
    }
  };

  const labelCls = "block text-sm font-medium text-gray-900 dark:text-white mb-1";
  const helpCls = "mt-1 text-xs text-gray-400 dark:text-gray-500";

  return (
    <div className="flex flex-col">
      {/* Standalone page header (hidden when embedded as a module tab). */}
      {!embedded && (
        <div className="flex items-center gap-2.5 min-w-0 pt-2 pb-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex-shrink-0 p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors"
            aria-label="Back"
          >
            <ArrowLeft size={18} />
          </button>
          <h1 className="text-base font-semibold text-gray-900 dark:text-white truncate">
            {field.label}
          </h1>
        </div>
      )}

      {apiError && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-error text-sm font-medium">
          {apiError}
        </div>
      )}

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 sm:p-5 max-w-xl">
        {pageLoading ? (
          <div className="space-y-4">
            {[0, 1].map((i) => (
              <div key={i} className="space-y-2">
                <div className="h-3.5 w-40 rounded bg-gray-100 dark:bg-gray-700 animate-pulse" />
                <div className="h-9 w-full rounded bg-gray-100 dark:bg-gray-700 animate-pulse" />
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Max files */}
            <div>
              <label htmlFor={`${field.key}-maxFiles`} className={labelCls}>
                Maximum files per upload
              </label>
              <input
                id={`${field.key}-maxFiles`}
                type="number"
                min={1}
                step={1}
                value={maxFiles}
                onChange={(e) => {
                  setMaxFiles(e.target.value);
                  setFieldErrors((p) => ({ ...p, maxFiles: "" }));
                }}
                onWheel={(e) => (e.target as HTMLInputElement).blur()}
                className={fieldCls(!!fieldErrors.maxFiles)}
              />
              {fieldErrors.maxFiles ? (
                <p className="mt-1 text-xs font-medium text-error leading-tight">
                  {fieldErrors.maxFiles}
                </p>
              ) : (
                <p className={helpCls}>How many files a user can attach at once.</p>
              )}
            </div>

            {/* Max file size (KB) */}
            <div>
              <label htmlFor={`${field.key}-maxFileSizeKb`} className={labelCls}>
                Maximum file size (KB)
              </label>
              <input
                id={`${field.key}-maxFileSizeKb`}
                type="number"
                min={1}
                step={1}
                value={maxFileSizeKb}
                onChange={(e) => {
                  setMaxFileSizeKb(e.target.value);
                  setFieldErrors((p) => ({ ...p, maxFileSizeKb: "" }));
                }}
                onWheel={(e) => (e.target as HTMLInputElement).blur()}
                className={fieldCls(!!fieldErrors.maxFileSizeKb)}
              />
              {fieldErrors.maxFileSizeKb ? (
                <p className="mt-1 text-xs font-medium text-error leading-tight">
                  {fieldErrors.maxFileSizeKb}
                </p>
              ) : (
                <p className={helpCls}>≈ {mbPreview} MB per file</p>
              )}
            </div>

            {/* Allowed types — read-only reference (managed in the File Type tab) */}
            {config?.allowedFileTypes && config.allowedFileTypes.length > 0 && (
              <div>
                <span className={labelCls}>Allowed file types</span>
                <div className="flex flex-wrap gap-2">
                  {config.allowedFileTypes.map((t) => (
                    <span
                      key={t}
                      className="px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-600 text-xs font-medium uppercase"
                    >
                      {t}
                    </span>
                  ))}
                </div>
                <p className={helpCls}>Manage these in the “File Type” tab.</p>
              </div>
            )}

            <div className="flex justify-end pt-1">
              <HeaderButtons
                onClick={handleSave}
                loading={saving}
                disabled={saving || pageLoading || !isDirty}
                icon={<Check size={13} />}
              >
                Save Changes
              </HeaderButtons>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadConfigDetail;
